/**
 * Utilidades de fecha para evitar bugs de timezone.
 * JavaScript interpreta "YYYY-MM-DD" como medianoche UTC, lo que desplaza
 * el día en zonas como Colombia (UTC-5). Estas funciones parsean fechas
 * como fecha local para mostrar correctamente.
 */

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD usando la zona horaria local del usuario.
 * Evita el bug donde new Date().toISOString() usa UTC (ej: 2 AM Colombia = día anterior en UTC).
 */
export const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formatea una fecha YYYY-MM-DD para mostrar sin desplazamiento de timezone.
 * Parsea la fecha como local (no UTC) para que "2026-02-14" muestre 14, no 13.
 */
export const formatAppointmentDate = (dateString) => {
    if (!dateString) return 'N/A';
    const str = (dateString || '').toString().split('T')[0];
    const parts = str.split('-');
    if (parts.length !== 3) return str;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1; // mes 0-indexed
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return str;
    const date = new Date(y, m, d);
    return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Formato compacto para tablas (ej: "27 jun 2026").
 */
export const formatAppointmentDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const str = (dateString || '').toString().split('T')[0];
    const parts = str.split('-');
    if (parts.length !== 3) return str;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return str;
    const date = new Date(y, m, d);
    return date.toLocaleDateString('es-CO', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Agrupa slots en items de visualización: slots disponibles como botones y rangos ocupados
 * como un solo bloque "X - Y Ocupado" en lugar de muchos slots bloqueados.
 * @param {Array} slots - [{ time_24, time_display, available }]
 * @param {number} slotStepMinutes - Intervalo entre slots (default 5)
 * @returns {Array} [{ type: 'available', slot } | { type: 'occupied', start, end, startDisplay, endDisplay }]
 */
export const getTimeSlotDisplayItems = (slots, slotStepMinutes = 5) => {
    if (!slots || slots.length === 0) return [];
    const addMinutes = (time24, mins) => {
        const parts = time24.split(':');
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        const total = h * 60 + m + mins;
        const nh = Math.floor(total / 60) % 24;
        const nm = total % 60;
        return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
    };
    const to12h = (time24) => {
        const parts = time24.split(':');
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        const period = h < 12 ? 'AM' : 'PM';
        const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
        return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
    };

    const items = [];
    let i = 0;
    while (i < slots.length) {
        const slot = slots[i];
        if (slot.available) {
            items.push({ type: 'available', slot });
            i++;
        } else {
            let j = i;
            while (j < slots.length && !slots[j].available) j++;
            const start = slot.time_24;
            const lastSlot = slots[j - 1];
            const end = addMinutes(lastSlot.time_24, slotStepMinutes);
            const slice = slots.slice(i, j);
            const hasInsufficientTime = slice.some(s => s.reason === 'insufficient_time');
            const hasBreak = slice.some(s => s.reason === 'break');
            const label = hasInsufficientTime
                ? 'Sin tiempo suficiente'
                : (hasBreak ? 'Descanso' : 'Ocupado');
            items.push({ type: 'occupied', start, end, startDisplay: slot.time_display, endDisplay: to12h(end), label });
            i = j;
        }
    }
    return items;
};
